import React from 'react';
import styles from '@/styles/components/question-builders/QuestionContent.module.css';
import { ActionButton } from '@ax2/common/components/ActionButton';
import { QuestionTypeDropdown } from './QuestionTypeDropdown';
import ModalButton from '@ax2/common/components/ModalButton';
import QuestionFlow, {
  getFlowStep,
} from '@ax2/takesurvey/components/question-flow/QuestionFlow';
import QuestionType from '@ax2/common/model/QuestionType';
import SurveyComplete from '../SurveyComplete';
import { useSurveyContext } from '../../contexts/SurveyContext';
import previewIcon from '../../assets/eyeball.svg';
import downIcon from '../../assets/arrow_down_dark.svg';
import { ScreenerPurposeElement } from '../ScreenerPurposeElement';
import ScreenerQuestionSchema from '@ax2/common/model/schemas/ScreenerQuestionSchema';
import { getScreenerLogic } from 'helpers/screenerLogic';
import QuestionSchema from '@ax2/common/model/schemas/QuestionSchema';
import {
  TemplateContentElement,
  TemplateContentElementData,
} from './base/TemplateContentElement';
import otherSpecifyImg from '../../assets/OTHERSPECIFY.png';
import { getPercentLabelForGrid } from 'helpers/results';
import ImageUploadModal from 'components/file-dnd/ImageUploadModal';

export interface QuestionInnerContentData {
  questionContentElement: JSX.Element;
  answerContentElement: JSX.Element;
  preselectedType: QuestionType;
}

export interface QuestionBaseContentData {
  question: QuestionSchema;
  isScreener: boolean;
  isTypeDisabled?: boolean;
  randomize?: QuestionRandomizeData;
  qNAAnswer?: QuestionNAAnswerTypeData;
  qPDAnswer?: QuestionPDAnswerTypeData;
}
export interface QuestionChangeData {
  onTypeChange?: (s: QuestionType) => void;
  saveCompletion: () => void;
}

export interface QuestionRandomizeData {
  disabled: boolean;
  value: boolean;
  onChange?: (v: boolean) => void;
}

export interface QuestionNAAnswerTypeData {
  disabled: boolean;
  state: boolean;
  onChangeState: (v: boolean) => void;
  templateData?: TemplateContentElementData;
}
export interface QuestionPDAnswerTypeData {
  disabled: boolean;
  state: boolean;
  onChangeState: (v: boolean) => void;
}

export const QuestionContent: React.FC<
  QuestionInnerContentData & QuestionBaseContentData & QuestionChangeData
> = props => {
  const surveySchema = useSurveyContext();
  const isScreener = props.isScreener;
  const screenerLogic = props.isScreener
    ? getScreenerLogic(props.question as ScreenerQuestionSchema)
    : null;

  const isRandomizeRow =
    props.question.questionType === QuestionType.CheckboxGrid ||
    props.question.questionType === QuestionType.RadioButtonGrid ||
    props.question.questionType === QuestionType.RadioButtonGridRow ||
    props.question.questionType === QuestionType.TextboxGridNumeric ||
    props.question.questionType === QuestionType.RatingList ||
    props.question.questionType === QuestionType.RankingList;

  const withAdditionalAnswerTypes =
    props.question.questionType === QuestionType.CheckboxList ||
    props.question.questionType === QuestionType.RadioButtonList ||
    props.question.questionType === QuestionType.CheckboxGrid ||
    props.question.questionType === QuestionType.RadioButtonGrid ||
    props.question.questionType === QuestionType.RadioButtonGridRow ||
    props.question.questionType === QuestionType.TextboxGridNumeric;

  const qPDAnswer = props.qPDAnswer;
  const qNAAnswer = props.qNAAnswer;

  const showGridLikeHelperText =
    props.question.questionType === QuestionType.CheckboxGrid ||
    props.question.questionType === QuestionType.RadioButtonGrid ||
    props.question.questionType === QuestionType.RadioButtonGridRow ||
    props.question.questionType === QuestionType.TextboxGridNumeric;
  const showListLikeHelperText =
    props.question.questionType === QuestionType.DropdownMenu ||
    props.question.questionType === QuestionType.CheckboxList ||
    props.question.questionType === QuestionType.RadioButtonList ||
    props.question.questionType === QuestionType.ContinuousSumList ||
    props.question.questionType === QuestionType.TextboxListNumeric ||
    props.question.questionType === QuestionType.TextboxListText;

  const showScaleLikeHelperText =
    props.question.questionType === QuestionType.RatingList;
  const showRankingLikeHelperText =
    props.question.questionType === QuestionType.RankingList;

  const gridRequiredStrategy =
    props.question &&
    'requiredOptions' in props.question &&
    props.question.requiredOptions &&
    'strategy' in props.question.requiredOptions &&
    props.question.requiredOptions.strategy;

  return (
    <div className={styles.questionBuilderContent}>
      <div className={styles.questionContentWrapper}>
        <div className={styles.questionContentRow}>
          <div className={styles.questionContent}>
            <span className={styles.title}>
              {isScreener ? 'Screener Question' : 'Survey Question'}
            </span>
            {props.questionContentElement}
          </div>
          <div className={styles.questionType}>
            <span className={styles.title}>
              {isScreener ? 'Screener Purpose' : 'Question Type'}
            </span>
            <div className={isScreener ? '' : styles.questionTypeContent}>
              {isScreener ? (
                <ScreenerPurposeElement
                  {...(props.question as ScreenerQuestionSchema)}
                  isFullHeight={true}
                />
              ) : (
                <>
                  <QuestionTypeDropdown
                    value={props.preselectedType}
                    disabled={props.isTypeDisabled}
                    onChange={props.onTypeChange}
                  />
                  <ImageUploadModal {...props} />
                </>
              )}
            </div>
          </div>
        </div>
        <div className={styles.questionContentSeparator}></div>
      </div>
      <div className={styles.answerContent}>
        <span className={styles.answersTitle}>Answers</span>
        {withAdditionalAnswerTypes && (
          <div className={styles.questionActions}>
            <ActionButton
              text={`${
                qNAAnswer && qNAAnswer.state ? '-' : '+'
              } NA/NONE OPTION`}
              type={qNAAnswer && qNAAnswer.state ? 'primary' : 'secondary'}
              disabled={!qNAAnswer || qNAAnswer.disabled}
              onClick={() =>
                qNAAnswer && qNAAnswer.onChangeState(!qNAAnswer.state)
              }
            />
            {withAdditionalAnswerTypes && (
              <ActionButton
                text={`${
                  qPDAnswer && qPDAnswer.state ? '-' : '+'
                } OTHER: SPECIFY`}
                type={qPDAnswer && qPDAnswer.state ? 'primary' : 'secondary'}
                disabled={!qPDAnswer || qPDAnswer.disabled}
                onClick={() =>
                  qPDAnswer && qPDAnswer.onChangeState(!qPDAnswer.state)
                }
              />
            )}
            {props.randomize && (
              <ActionButton
                text={`${props.randomize.value ? '-' : '+'} Randomize ${
                  isRandomizeRow ? 'Row ' : ''
                }`}
                type={props.randomize.value ? 'primary' : 'secondary'}
                disabled={props.randomize.disabled}
                onClick={() =>
                  props.randomize &&
                  props.randomize.onChange &&
                  props.randomize.onChange(!props.randomize.value)
                }
              />
            )}
          </div>
        )}
        {props.answerContentElement}
        {screenerLogic && (
          <div className={styles.logicContent}>
            <div className={styles.questionContentSeparator}></div>
            <span className={styles.logicTitle}>Logic</span>
            {screenerLogic.continueText && (
              <div className={styles.continueLogic}>
                <span>{`${screenerLogic.continueText} = `}</span>
                <span className={styles.continueTitle}>Continue</span>
              </div>
            )}
            {screenerLogic.terminateText && (
              <div className={styles.terminateLogic}>
                <span>{`${screenerLogic.terminateText} = `}</span>
                <span className={styles.terminateTitle}>Terminate</span>
              </div>
            )}
          </div>
        )}
        {showGridLikeHelperText && (
          <div className={styles.answerTypeContent}>
            <span className={styles.answersTypeDescr}>
              {gridRequiredStrategy &&
              getPercentLabelForGrid(gridRequiredStrategy) === 'Column'
                ? 'Please use the Row text for the variables you are testing and the Column text for the comparators. The results will be calculated as column percentages. Give the respondent a choice: specify at least 2 columns and 2 rows.'
                : `Please use the Column text for the variables you are testing and the Row text for the comparators. The results will be calculated as row percentages. Give the respondent a choice: specify at least 2 columns and 2 rows.`}
            </span>
          </div>
        )}
        {showListLikeHelperText && (
          <div className={styles.answerTypeContent}>
            <span className={styles.answersTypeDescr}>
              {'More than one answer option is required.'}
            </span>
          </div>
        )}
        {showScaleLikeHelperText && (
          <div className={styles.answerTypeContent}>
            <span className={styles.answersTypeDescr}>
              {'At least two scale points are required.'}
            </span>
          </div>
        )}
        {showRankingLikeHelperText && (
          <div className={styles.answerTypeContent}>
            <span className={styles.answersTypeDescr}>
              {'At least three ranks are required.'}
            </span>
          </div>
        )}
        {qPDAnswer && qPDAnswer.state && (
          <div className={styles.answerTypeContent}>
            <div className={styles.questionContentSeparator}></div>
            <span className={styles.answersTypeTitle}>Other: specify</span>
            <span className={styles.answersTypeDescr}>
              {
                'Other (specify) is turned on for this question. When a respondent selects this option, they will be required to specify their responses in a text field.'
              }
            </span>
            <img
              className={styles.otherSpecifyExampleImg}
              src={otherSpecifyImg}
              alt="other: specify example"
            />
          </div>
        )}
        {qNAAnswer && qNAAnswer.state && qNAAnswer.templateData && (
          <div className={styles.answerTypeContent}>
            <div className={styles.questionContentSeparator}></div>
            <span className={styles.answersTypeTitle}>N/A answer option</span>
            <span className={styles.answersTypeDescr}>
              {
                'Specify a label for NA/None option that the respondent should see. “Not applicable” is used by default.'
              }
            </span>
            <div
              className={
                props.question.isLocked
                  ? styles.naAnswerLockedTextContainer
                  : styles.naAnswerTextContainer
              }
            >
              <TemplateContentElement {...qNAAnswer.templateData} />
            </div>
          </div>
        )}
      </div>
      <div className={styles.footerActions}>
        {surveySchema && (
          <ModalButton
            text="Preview"
            type="secondaryAlt"
            icon={{
              source: previewIcon,
              size: 'xnormal',
            }}
            disabled={props.preselectedType === QuestionType.Empty}
            content={doClose => (
              <QuestionFlow
                screeners={isScreener ? surveySchema.screeners : []}
                questions={isScreener ? [] : surveySchema.questions}
                header={{
                  title: 'Preview',
                  isSubtitle: true,
                }}
                activeStepNumber={getFlowStep(
                  isScreener ? surveySchema.screeners : surveySchema.questions,
                  isScreener ? [] : surveySchema.questions,
                  props.question.id
                )}
                isFastPreview={true}
                finalStep={{
                  name: 'SEND',
                  content: isScreener
                    ? undefined
                    : function fn(doBack) {
                        return (
                          <SurveyComplete
                            surveyName={
                              surveySchema.title ? surveySchema.title : ''
                            }
                            panel={surveySchema.panel}
                            back={{
                              title: 'Back',
                              onAction: doBack,
                            }}
                            complete={{
                              title: 'Send Panel Survey',
                              onAction: doClose,
                            }}
                          />
                        );
                      },
                }}
              />
            )}
          />
        )}
        <ActionButton
          text="Save & go to next question"
          type="secondaryAlt"
          icon={{
            source: downIcon,
            size: 'xsmall',
            position: 'right',
          }}
          onClick={props.saveCompletion}
        />
      </div>
    </div>
  );
};
